import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCard, Truck, MapPin, QrCode, AlertCircle } from 'lucide-react';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// West Rand areas for local delivery
const WEST_RAND_AREAS = [
  'krugersdorp', 'randfontein', 'westonaria', 'roodepoort', 'mogale city',
  'kagiso', 'munsieville', 'lewisham', 'toekomsrus', 'mohlakeng',
  'bekkersdal', 'carletonville', 'fochville', 'wedela', 'khutsong',
  'merafong', 'oberholzer', 'greenspark', 'florida', 'florida hills',
  'florida north', 'florida glen', 'roodepoort west', 'horizon view',
  'ruimsig', 'muldersdrift', 'lanseria', 'honeydew', 'little falls',
  'strubensvalley', 'wilgeheuwel', 'clearwater', 'featherbrooke'
];

const CheckoutPage = () => {
  const { cart, getCartTotal } = useCart();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);
  const [shippingOption, setShippingOption] = useState('nationwide');
  const [paymentMethod, setPaymentMethod] = useState('yoco');
  const [isWestRand, setIsWestRand] = useState(false);
  const [snapScanDetails, setSnapScanDetails] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [discountValid, setDiscountValid] = useState(null); // { valid, amount, combo_only }
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [subscribeToNewsletter, setSubscribeToNewsletter] = useState(true);
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    province: '',
  });

  const cartTotal = getCartTotal();
  const FREE_SHIPPING_THRESHOLD = 600;
  const qualifiesForFreeShipping = cartTotal >= FREE_SHIPPING_THRESHOLD;
  
  // Shipping costs
  const NATIONWIDE_RATE = qualifiesForFreeShipping ? 0 : 99;
  const LOCAL_RATE = 49;
  
  const shippingCost = shippingOption === 'local' ? LOCAL_RATE : NATIONWIDE_RATE;
  
  // Check if cart has combo for discount eligibility
  const hasCombo = cart.some(item => item.is_combo);
  const discountAmount = (discountValid && discountValid.valid && (!discountValid.combo_only || hasCombo)) ? discountValid.amount : 0;
  const orderTotal = Math.max(0, cartTotal - discountAmount) + shippingCost;

  useEffect(() => {
    if (cart.length === 0 && !searchParams.get('cancelled')) {
      toast.error('Your cart is empty');
      navigate('/cart');
    }

    if (searchParams.get('cancelled')) {
      toast.error('Payment was cancelled');
    }

    // Auto-fill referral discount code from sessionStorage
    const referralCode = sessionStorage.getItem('referral_discount_code');
    if (referralCode && !discountCode) {
      setDiscountCode(referralCode);
      // Auto-validate it
      axios.post(`${API}/discount/validate`, { code: referralCode })
        .then(res => {
          setDiscountValid(res.data);
          toast.success(`Referral discount of R${res.data.amount} applied!`);
        })
        .catch(() => {});
    }
  }, [cart, navigate, searchParams]);

  // Check if city is in West Rand
  useEffect(() => {
    const cityLower = formData.city.toLowerCase().trim();
    const isInWestRand = WEST_RAND_AREAS.some(area => 
      cityLower.includes(area) || area.includes(cityLower)
    );
    setIsWestRand(isInWestRand);
    
    // Reset to nationwide if not in West Rand and local was selected
    if (!isInWestRand && shippingOption === 'local') {
      setShippingOption('nationwide');
    }
  }, [formData.city, shippingOption]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Save cart session for abandoned cart recovery when email is entered
  const handleEmailBlur = async () => {
    if (formData.customerEmail && cart.length > 0) {
      try {
        await axios.post(`${API}/cart/session`, {
          email: formData.customerEmail,
          customer_name: formData.customerName,
          items: cart.map(item => ({ product_id: item.id, name: item.name, quantity: item.quantity, price: item.price })),
        });
      } catch {
        // Silent fail - don't block checkout
      }
    }
  };

  const applyDiscountCode = async () => {
    if (!discountCode.trim()) return;
    setValidatingDiscount(true);
    try {
      const res = await axios.post(`${API}/discount/validate`, { code: discountCode.trim() });
      setDiscountValid(res.data);
      if (res.data.combo_only && !hasCombo) {
        toast.error('This code is valid for combo purchases only. Add a combo to your cart!');
      } else {
        toast.success(`R${res.data.amount} discount applied!`);
      }
    } catch (err) {
      setDiscountValid(null);
      toast.error(err.response?.data?.detail || 'Invalid discount code');
    } finally {
      setValidatingDiscount(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setSnapScanDetails(null);
    
    console.log('[CHECKOUT] Starting checkout process...');
    console.log('[CHECKOUT] Payment method:', paymentMethod);
    console.log('[CHECKOUT] Shipping option:', shippingOption);

    try {
      // Create order first
      console.log('[CHECKOUT] Creating order...');
      const orderData = {
        customer_email: formData.customerEmail,
        customer_name: formData.customerName,
        items: cart.map((item) => ({
          product_id: item.id,
          quantity: item.quantity,
          price: item.price,
        })),
        shipping_address: {
          address: formData.address,
          city: formData.city,
          postal_code: formData.postalCode,
          province: formData.province,
          phone: formData.phone,
        },
        shipping_option: shippingOption,
        shipping_cost: shippingCost,
        discount_code: discountAmount > 0 ? discountCode.trim().toUpperCase() : null,
      };

      const orderResponse = await axios.post(`${API}/orders`, orderData);
      const order = orderResponse.data;
      console.log('[CHECKOUT] Order created:', order.id);

      // Fire-and-forget newsletter subscribe (silent on duplicate / network errors)
      if (subscribeToNewsletter && formData.customerEmail) {
        axios.post(`${API}/newsletter/subscribe`, {
          email: formData.customerEmail.trim().toLowerCase(),
          source: 'checkout',
        }).catch(() => { /* ignore — duplicates return 409, network errors are non-fatal */ });
      }

      if (paymentMethod === 'yoco') {
        // Create Yoco checkout session
        console.log('[CHECKOUT] Creating Yoco checkout session...');
        
        // Include order_id in the success URL so we can look up payment status
        const checkoutData = {
          order_id: order.id,
          success_url: `${window.location.origin}/order-success?order_id=${order.id}`,
          cancel_url: `${window.location.origin}/checkout?cancelled=true`,
        };

        const checkoutResponse = await axios.post(`${API}/payment/yoco/checkout`, checkoutData);
        console.log('[CHECKOUT] Yoco checkout created:', checkoutResponse.data.checkout_id);
        
        // Store both checkout_id and order_id in localStorage as backup
        localStorage.setItem('pendingCheckoutId', checkoutResponse.data.checkout_id);
        localStorage.setItem('pendingOrderId', order.id);
        
        // Redirect to Yoco payment page
        if (checkoutResponse.data.redirect_url) {
          window.location.href = checkoutResponse.data.redirect_url;
        } else {
          // For test mode without real Yoco keys, redirect to success with order_id
          toast.info('Test mode: Redirecting to order confirmation');
          navigate(`/order-success?order_id=${order.id}`);
        }
      } else if (paymentMethod === 'snapscan') {
        // Generate SnapScan payment details
        console.log('[CHECKOUT] Generating SnapScan payment...');
        const snapResponse = await axios.post(`${API}/payment/snapscan/generate`, {
          order_id: order.id
        });
        
        console.log('[CHECKOUT] SnapScan details:', snapResponse.data);
        setSnapScanDetails({
          ...snapResponse.data,
          order_id: order.id
        });
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('[CHECKOUT ERROR]', error);
      console.error('[CHECKOUT ERROR] Response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to process checkout. Please try again.');
      setIsProcessing(false);
    }
  };

  // If SnapScan details are shown, render SnapScan payment view
  if (snapScanDetails) {
    return (
      <div className="min-h-screen bg-bone-white">
        <Navigation />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="bg-white rounded-3xl p-8 shadow-soft text-center">
            <div className="w-20 h-20 bg-perfect-peach/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <QrCode className="w-10 h-10 text-perfect-peach" />
            </div>
            
            <h1 className="font-playfair text-3xl font-semibold text-[#2D2420] mb-4">
              Pay with SnapScan
            </h1>
            
            <div className="bg-bone-white rounded-2xl p-6 mb-6">
              <p className="text-african-earth mb-4">
                Open your SnapScan app and scan to pay
              </p>
              
              {snapScanDetails.snapscan_url && (
                <a
                  href={snapScanDetails.snapscan_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-[#00A3E0] text-white text-center px-6 py-4 rounded-xl font-semibold text-lg hover:bg-[#0090c7] transition-all duration-300 mb-4"
                  data-testid="snapscan-pay-link"
                >
                  Open SnapScan to Pay
                </a>
              )}
              
              <div className="space-y-3 text-left">
                <div className="flex justify-between">
                  <span className="text-african-earth">Amount:</span>
                  <span className="font-semibold text-[#2D2420]">R{snapScanDetails.amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-african-earth">Reference:</span>
                  <span className="font-mono font-semibold text-perfect-peach">{snapScanDetails.payment_ref}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800 text-left">
                  <strong>Important:</strong> Please include the reference <span className="font-mono font-semibold">{snapScanDetails.payment_ref}</span> in your SnapScan payment. We'll verify your payment and send a confirmation email.
                </p>
              </div>
            </div>
            
            <p className="text-african-earth/60 text-sm mb-6">
              Once payment is received, you'll receive an order confirmation email within 24 hours.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  setSnapScanDetails(null);
                  navigate('/');
                }}
                className="bg-perfect-peach text-white px-8 py-3 rounded-full font-medium hover:bg-peach-dark transition-all duration-300"
              >
                Done
              </button>
              <button
                onClick={() => setSnapScanDetails(null)}
                className="border-2 border-african-earth text-african-earth px-8 py-3 rounded-full font-medium hover:bg-african-earth hover:text-white transition-all duration-300"
              >
                Change Payment Method
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone-white">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="text-center mb-8">
          <h1 className="font-playfair text-4xl sm:text-5xl font-semibold text-[#2D2420] mb-3">
            Complete Your Order
          </h1>
          <p className="text-african-earth text-lg">
            Your wellness journey is just a few steps away
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-6 sm:p-8 shadow-soft">
              <h2 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-2">
                Delivery Details
              </h2>
              <p className="text-african-earth text-sm mb-6">
                Where should we send your wellness essentials?
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-african-earth font-medium mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-[#E0D5D0] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-perfect-peach"
                    data-testid="customer-name-input"
                  />
                </div>

                <div>
                  <label className="block text-african-earth font-medium mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="customerEmail"
                    value={formData.customerEmail}
                    onChange={handleInputChange}
                    onBlur={handleEmailBlur}
                    required
                    className="w-full border border-[#E0D5D0] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-perfect-peach"
                    data-testid="customer-email-input"
                  />
                  <label className="mt-2.5 flex items-start gap-2.5 cursor-pointer group" data-testid="newsletter-optin-label">
                    <input
                      type="checkbox"
                      checked={subscribeToNewsletter}
                      onChange={(e) => setSubscribeToNewsletter(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-[#E0D5D0] text-perfect-peach focus:ring-perfect-peach focus:ring-offset-0 accent-perfect-peach cursor-pointer"
                      data-testid="newsletter-optin-checkbox"
                    />
                    <span className="text-sm text-african-earth group-hover:text-[#2D2420] transition-colors leading-snug">
                      Send me <strong className="text-perfect-peach">10% off my next order</strong> over R500, plus early access to drops.
                    </span>
                  </label>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-african-earth font-medium mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-[#E0D5D0] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-perfect-peach"
                    data-testid="phone-input"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-african-earth font-medium mb-2">
                    Street Address *
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-[#E0D5D0] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-perfect-peach"
                    data-testid="address-input"
                  />
                </div>

                <div>
                  <label className="block text-african-earth font-medium mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-[#E0D5D0] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-perfect-peach"
                    data-testid="city-input"
                  />
                </div>

                <div>
                  <label className="block text-african-earth font-medium mb-2">
                    Province *
                  </label>
                  <select
                    name="province"
                    value={formData.province}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-[#E0D5D0] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-perfect-peach"
                    data-testid="province-select"
                  >
                    <option value="">Select Province</option>
                    <option value="Gauteng">Gauteng</option>
                    <option value="Western Cape">Western Cape</option>
                    <option value="Eastern Cape">Eastern Cape</option>
                    <option value="KwaZulu-Natal">KwaZulu-Natal</option>
                    <option value="Limpopo">Limpopo</option>
                    <option value="Mpumalanga">Mpumalanga</option>
                    <option value="North West">North West</option>
                    <option value="Northern Cape">Northern Cape</option>
                    <option value="Free State">Free State</option>
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-african-earth font-medium mb-2">
                    Postal Code *
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-[#E0D5D0] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-perfect-peach"
                    data-testid="postal-code-input"
                  />
                </div>
              </div>

              {/* Shipping Options */}
              <div className="mt-8 pt-6 border-t border-[#E0D5D0]">
                <h3 className="font-playfair text-xl font-semibold text-[#2D2420] mb-4">
                  Delivery Option
                </h3>
                
                {/* West Rand Badge - only show if eligible */}
                {isWestRand && (
                  <div className="mb-4">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#6B7D6B] bg-[#E8EDE8] px-3 py-1.5 rounded-full">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      West Rand delivery available — faster & only R49
                    </span>
                  </div>
                )}
                
                <div className="space-y-3">
                  {/* Nationwide Option */}
                  <label 
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      shippingOption === 'nationwide' 
                        ? 'border-perfect-peach bg-perfect-peach/5' 
                        : 'border-[#E0D5D0] hover:border-peach-light'
                    }`}
                    data-testid="shipping-nationwide"
                  >
                    <input
                      type="radio"
                      name="shipping"
                      value="nationwide"
                      checked={shippingOption === 'nationwide'}
                      onChange={() => setShippingOption('nationwide')}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      shippingOption === 'nationwide' ? 'border-perfect-peach' : 'border-african-earth/30'
                    }`}>
                      {shippingOption === 'nationwide' && (
                        <div className="w-2.5 h-2.5 rounded-full bg-perfect-peach" />
                      )}
                    </div>
                    <Truck className="w-5 h-5 text-african-earth" />
                    <div className="flex-1">
                      <p className="font-medium text-[#2D2420]">Nationwide Courier</p>
                      <p className="text-sm text-african-earth">3–5 business days</p>
                    </div>
                    <div className="text-right">
                      {qualifiesForFreeShipping ? (
                        <span className="font-semibold text-perfect-peach">FREE</span>
                      ) : (
                        <span className="font-semibold text-[#2D2420]">R99</span>
                      )}
                    </div>
                  </label>

                  {/* Local West Rand Option - Only show if in West Rand */}
                  {isWestRand && (
                    <label 
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        shippingOption === 'local' 
                          ? 'border-perfect-peach bg-perfect-peach/5' 
                          : 'border-[#E0D5D0] hover:border-peach-light'
                      }`}
                      data-testid="shipping-local"
                    >
                      <input
                        type="radio"
                        name="shipping"
                        value="local"
                        checked={shippingOption === 'local'}
                        onChange={() => setShippingOption('local')}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        shippingOption === 'local' ? 'border-perfect-peach' : 'border-african-earth/30'
                      }`}>
                        {shippingOption === 'local' && (
                          <div className="w-2.5 h-2.5 rounded-full bg-perfect-peach" />
                        )}
                      </div>
                      <MapPin className="w-5 h-5 text-african-earth" />
                      <div className="flex-1">
                        <p className="font-medium text-[#2D2420]">Local Delivery (West Rand)</p>
                        <p className="text-sm text-african-earth">1–2 business days</p>
                      </div>
                      <span className="font-semibold text-[#2D2420]">R49</span>
                    </label>
                  )}
                </div>

                {/* Free shipping notice */}
                {!qualifiesForFreeShipping && (
                  <p className="mt-3 text-sm text-african-earth/70">
                    Add R{(FREE_SHIPPING_THRESHOLD - cartTotal).toFixed(0)} more for free nationwide shipping
                  </p>
                )}
              </div>

              {/* Payment Method Selection */}
              <div className="mt-8 pt-6 border-t border-[#E0D5D0]">
                <h3 className="font-playfair text-xl font-semibold text-[#2D2420] mb-4">
                  Payment Method
                </h3>
                
                <div className="space-y-3">
                  {/* Yoco Card Payment */}
                  <label 
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      paymentMethod === 'yoco' 
                        ? 'border-perfect-peach bg-perfect-peach/5' 
                        : 'border-[#E0D5D0] hover:border-peach-light'
                    }`}
                    data-testid="payment-yoco"
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="yoco"
                      checked={paymentMethod === 'yoco'}
                      onChange={() => setPaymentMethod('yoco')}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === 'yoco' ? 'border-perfect-peach' : 'border-african-earth/30'
                    }`}>
                      {paymentMethod === 'yoco' && (
                        <div className="w-2.5 h-2.5 rounded-full bg-perfect-peach" />
                      )}
                    </div>
                    <CreditCard className="w-5 h-5 text-african-earth" />
                    <div className="flex-1">
                      <p className="font-medium text-[#2D2420]">Pay with Card (Yoco)</p>
                      <p className="text-sm text-african-earth">Visa, Mastercard accepted</p>
                    </div>
                  </label>

                  {/* SnapScan Payment */}
                  <label 
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      paymentMethod === 'snapscan' 
                        ? 'border-perfect-peach bg-perfect-peach/5' 
                        : 'border-[#E0D5D0] hover:border-peach-light'
                    }`}
                    data-testid="payment-snapscan"
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="snapscan"
                      checked={paymentMethod === 'snapscan'}
                      onChange={() => setPaymentMethod('snapscan')}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === 'snapscan' ? 'border-perfect-peach' : 'border-african-earth/30'
                    }`}>
                      {paymentMethod === 'snapscan' && (
                        <div className="w-2.5 h-2.5 rounded-full bg-perfect-peach" />
                      )}
                    </div>
                    <QrCode className="w-5 h-5 text-african-earth" />
                    <div className="flex-1">
                      <p className="font-medium text-[#2D2420]">Pay with SnapScan</p>
                      <p className="text-sm text-african-earth">Scan QR code to pay</p>
                    </div>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={isProcessing}
                className="w-full mt-8 bg-perfect-peach text-white px-8 py-4 rounded-full font-medium text-lg hover:bg-peach-dark transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="proceed-to-payment-button"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    Processing...
                  </>
                ) : (
                  <>
                    {paymentMethod === 'yoco' ? <CreditCard className="w-5 h-5" /> : <QrCode className="w-5 h-5" />}
                    {paymentMethod === 'yoco' ? 'Continue to Secure Payment' : 'Generate SnapScan Code'}
                  </>
                )}
              </button>
              
              <p className="text-center text-african-earth/60 text-sm mt-4">
                Your information is protected and will be handled with care
              </p>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl p-6 shadow-soft sticky top-24">
              <h3 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-6">
                Your Order
              </h3>
              <div className="space-y-4 mb-6">
                {cart.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm border-b border-bone-white pb-3">
                    <span className="text-african-earth">
                      {item.quantity}x {item.name}
                    </span>
                    <span className="font-medium text-[#2D2420]">
                      R{(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
                
                {/* Subtotal */}
                <div className="flex justify-between text-sm pt-2">
                  <span className="text-african-earth">Subtotal</span>
                  <span className="text-[#2D2420]">R{cartTotal.toFixed(2)}</span>
                </div>
                
                {/* Shipping */}
                <div className="flex justify-between text-sm">
                  <span className="text-african-earth">Shipping</span>
                  <span className={shippingCost === 0 ? 'text-perfect-peach font-medium' : 'text-[#2D2420]'}>
                    {shippingCost === 0 ? 'FREE' : `R${shippingCost.toFixed(2)}`}
                  </span>
                </div>

                {/* Discount Code */}
                <div className="pt-2">
                  <label className="block text-xs text-african-earth mb-1.5">Discount Code</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value)}
                      placeholder="Enter code"
                      disabled={discountValid?.valid}
                      className="flex-1 border border-[#E0D5D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-perfect-peach disabled:bg-bone-white"
                      data-testid="discount-code-input"
                    />
                    {discountValid?.valid ? (
                      <span className="flex items-center text-green-600 text-xs font-medium px-2">Applied</span>
                    ) : (
                      <button
                        type="button"
                        onClick={applyDiscountCode}
                        disabled={validatingDiscount || !discountCode.trim()}
                        className="bg-[#2D2420] text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-[#3d342e] transition-colors disabled:opacity-50"
                        data-testid="apply-discount-button"
                      >
                        {validatingDiscount ? '...' : 'Apply'}
                      </button>
                    )}
                  </div>
                  {discountValid?.valid && discountValid.combo_only && !hasCombo && (
                    <p className="text-xs text-yellow-600 mt-1">This code is for combo purchases only</p>
                  )}
                </div>

                {/* Discount Line */}
                {discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount ({discountValid?.code})</span>
                    <span>-R{discountAmount.toFixed(2)}</span>
                  </div>
                )}

                {/* Total */}
                <div className="border-t border-[#E0D5D0] pt-4 mt-4">
                  <div className="flex justify-between text-xl">
                    <span className="font-playfair font-semibold text-[#2D2420]">Total</span>
                    <span className="font-bold text-perfect-peach" data-testid="checkout-total">
                      R{orderTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-bone-white rounded-xl p-4 text-sm text-african-earth space-y-2">
                <p className="flex items-start gap-2">
                  <span className="text-perfect-peach mt-0.5">✓</span>
                  <span>Secure payment via Yoco</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="text-perfect-peach mt-0.5">✓</span>
                  <span>Prepared within 1-2 business days</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="text-perfect-peach mt-0.5">✓</span>
                  <span>Discreet, respectful packaging</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
